/**
 * Category Service
 *
 * API client for category-related endpoints.
 */

import { fetchApi } from './api';
import type {
  CategoryRead,
  CategoryCreate,
  CategoryUpdate,
  CategoryTree,
  CategoryUsage,
  CategoryRuleRead,
  CategoryRuleCreate,
  CategoryRuleUpdate,
  CategoryRuleWithCategory,
  CategoryRuleTestRequest,
  CategoryRuleTestResponse,
  PaginatedResponse,
} from '@/shared/types';

export interface CategoryListParams {
  include_system?: boolean;
  parent_id?: string;
  is_income?: boolean;
  page?: number;
  page_size?: number;
}

export interface CategoryRuleListParams {
  category_id?: string;
  is_enabled?: boolean;
  page?: number;
  page_size?: number;
}

export const categoryService = {
  // =========================================================================
  // Categories
  // =========================================================================

  /**
   * List all categories
   */
  list: (params?: CategoryListParams) =>
    fetchApi<PaginatedResponse<CategoryRead>>('/categories', {
      params: params as Record<string, unknown>,
    }),

  /**
   * Get categories as a tree structure
   */
  getTree: () =>
    fetchApi<CategoryTree[]>('/categories/tree'),

  /**
   * Get transaction usage counts per category
   */
  getUsage: () =>
    fetchApi<CategoryUsage[]>('/categories/usage'),

  /**
   * Get a single category by ID
   */
  getById: (id: string) =>
    fetchApi<CategoryRead>(`/categories/${id}`),

  /**
   * Create a new category
   */
  create: (data: CategoryCreate) =>
    fetchApi<CategoryRead>('/categories', { method: 'POST', body: data }),

  /**
   * Update a category
   */
  update: (id: string, data: CategoryUpdate) =>
    fetchApi<CategoryRead>(`/categories/${id}`, { method: 'PATCH', body: data }),

  /**
   * Delete a category
   */
  delete: (id: string) =>
    fetchApi<void>(`/categories/${id}`, { method: 'DELETE' }),

  // =========================================================================
  // Category Rules
  // =========================================================================

  /**
   * List category rules
   */
  listRules: (params?: CategoryRuleListParams) =>
    fetchApi<PaginatedResponse<CategoryRuleWithCategory>>('/category-rules', {
      params: params as Record<string, unknown>,
    }),

  /**
   * Get a single category rule by ID
   */
  getRuleById: (id: string) =>
    fetchApi<CategoryRuleRead>(`/category-rules/${id}`),

  /**
   * Create a new category rule
   */
  createRule: (data: CategoryRuleCreate) =>
    fetchApi<CategoryRuleRead>('/category-rules', { method: 'POST', body: data }),

  /**
   * Update a category rule
   */
  updateRule: (id: string, data: CategoryRuleUpdate) =>
    fetchApi<CategoryRuleRead>(`/category-rules/${id}`, { method: 'PATCH', body: data }),

  /**
   * Delete a category rule
   */
  deleteRule: (id: string) =>
    fetchApi<void>(`/category-rules/${id}`, { method: 'DELETE' }),

  /**
   * Apply a single rule to all uncategorized transactions
   */
  applyRule: (id: string) =>
    fetchApi<{ matched_count: number }>(`/category-rules/${id}/apply`, { method: 'POST', body: {} }),

  /**
   * Apply all enabled rules to uncategorized transactions in priority order
   */
  applyAllRules: () =>
    fetchApi<{ matched_count: number }>('/category-rules/apply-all', { method: 'POST', body: {} }),

  /**
   * Test a category rule pattern against real transactions (read-only)
   */
  testRule: (data: CategoryRuleTestRequest) =>
    fetchApi<CategoryRuleTestResponse>('/category-rules/test', {
      method: 'POST',
      body: data,
    }),
};
